import type { FC } from 'react';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import type { SpaceSelectorProps } from '@confluence/space-selector';
import { SpaceSelector } from '@confluence/space-selector';

export const i18n = defineMessages({
	spaceSelectorLabel: {
		id: 'template-panel.space-selector.label',
		defaultMessage: 'Select a template from this space',
		description:
			'Label text above a space selector. The space selector is used as a filter for templates listed under it.',
	},
});

const TEMPLATE_PANEL_SPACE_SELECTOR = 'template-panel-space-selector';

const spaceSelectorWrapperStyles = xcss({
	marginBottom: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpaceSelectorLabel = styled.label({
	font: token('font.body.small'),
	fontWeight: token('font.weight.bold'),
	color: token('color.text.subtle'),
	marginTop: token('space.150'),
	marginBottom: token('space.050'),
});

// A space must include at least one of these permissions in order for us to list it in the Template Panel
const requiredSpaceOperations = [
	{
		targetType: 'page',
		operation: 'create',
	},
	{
		targetType: 'blogpost',
		operation: 'create',
	},
];

type TemplatePanelSpaceSelectorProps = {
	onChange: SpaceSelectorProps['onChange'];
	spaceKey: string;
	isDisabled: boolean;
};

export const TemplatePanelSpaceSelector: FC<TemplatePanelSpaceSelectorProps> = ({
	onChange,
	spaceKey,
	isDisabled,
}) => {
	return (
		<ErrorBoundary attribution={Attribution.PAGE_MODES}>
			<Box xcss={spaceSelectorWrapperStyles}>
				<SpaceSelectorLabel htmlFor={TEMPLATE_PANEL_SPACE_SELECTOR}>
					<FormattedMessage {...i18n.spaceSelectorLabel} />
				</SpaceSelectorLabel>
				<SpaceSelector
					onChange={onChange}
					spaceKey={spaceKey}
					isDisabled={isDisabled}
					operationsUsedToDetermineRestrictedStatus={requiredSpaceOperations}
					showRestrictedSpaces={false}
					filterDefaultFromResults={false}
					showSpaceLabel={false}
					filterStarredFromRecent
					selectSpacing="compact"
					id={TEMPLATE_PANEL_SPACE_SELECTOR}
				/>
			</Box>
		</ErrorBoundary>
	);
};
